import React from 'react';
import styled, { css } from 'styled-components';
import { Instruction } from 'ducks/assemblyInstructions';
import Body from 'ui/Typography/Body';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import media from 'ui/media';
import { isMobile } from 'utils/platform';
import ProductIdentifier from '@ingka/product-identifier';
import { ProductUtility } from '@inter-ikea-kompis/utilities';

const SharedTextStyle = css`
  margin: 0 ${({ theme }) => theme.space.xxs};
  text-transform: capitalize;
`;

const StyledTable = styled.table`
  margin: 0 0 0 ${({ theme }) => theme.space.md};
  width: 55%;
  ${media.mobile`
    width: 100%;
    margin: 0;
  `}
  tr {
    &:first-child {
      border-bottom: 1px solid ${({ theme }) => theme.colors.grey[300]};
    }

    display: flex;
    justify-content: space-between;

    th {
      margin: 0 0 ${({ theme }) => theme.space.xxs} 0;
      font-size: ${({ theme }) => theme.space.s};
      font-weight: normal;
      p {
        text-transform: capitalize;
      }
    }

    td {
      display: grid;
      grid-template-columns: repeat(3, auto);
      font-size: ${({ theme }) => theme.space.md};
      margin-top: ${({ theme }) => theme.space.xs};
      margin-bottom: ${({ theme }) => theme.space.xs};
      align-items: center;

      span {
        &:first-child {
          margin-left: 0;
        }

        margin-left: ${({ theme }) => theme.space.xxs};
        margin-right: ${({ theme }) => theme.space.xxs};
      }
    }
  }
`;

const Description = styled(Body)`
  ${SharedTextStyle};
  color: ${({ theme }) => theme.colors.grey[700]};
`;

const HoleNumberTitle = styled(Body)`
  ${SharedTextStyle};
  color: ${({ theme }) => theme.colors.grey[700]};
`;

const ProductNameTitle = styled(Body)`
  ${SharedTextStyle};
  font-weight: bold;
  text-transform: uppercase;
`;

type Props = {
  instructions: Array<Instruction>;
  headers: string[];
};

function Table({ instructions, headers }: Props) {
  return (
    <StyledTable>
      <tbody>
        <tr>
          {headers.map((header: string, index) => (
            <th key={index}>
              <Body type={ThemeFontStyleTypeEnum.bodySmall}>{header}</Body>
            </th>
          ))}
        </tr>
        {instructions &&
          instructions.map((instructions, index) => (
            <tr key={index}>
              <td style={{ width: 30, alignItems: 'flex-start' }}>
                <HoleNumberTitle type={ThemeFontStyleTypeEnum.bodyMedium}>
                  {instructions.holeNumber}
                </HoleNumberTitle>
              </td>
              <td
                style={{
                  display: 'flex',
                  flexDirection: isMobile() ? 'column' : 'row',
                  alignItems: 'flex-start',
                  flex: '1 0',
                  flexWrap: 'wrap'
                }}
              >
                <ProductNameTitle type={ThemeFontStyleTypeEnum.bodyMedium}>
                  {instructions.productName}
                </ProductNameTitle>
                <Description type={ThemeFontStyleTypeEnum.bodyMedium}>
                  {instructions.description}
                </Description>
              </td>
              <td style={{ alignItems: 'flex-start' }}>
                <ProductIdentifier
                  value={ProductUtility.formatItemNo(instructions.articleNumber)}
                />
              </td>
            </tr>
          ))}
      </tbody>
    </StyledTable>
  );
}

export default Table;
